import { TemplateImageDto } from '@/features/dashboard/templateImages/types/TemplateImageDto.ts';

// Dependency-injected image capability for `RichTextEditor`. Passing this prop
// turns on the image toolbar button, the media-library picker and the
// `{{image '<id>'}}` ↔ `<img>` converter roundtrip. The core stays decoupled
// from the feature data layer — consumers wire the API hooks (see
// `useTemplateImageSupport`) and hand the editor this object.
export interface TemplateImageSupport {
  // Media library of the current tenant (READY images with signed preview URLs).
  images: TemplateImageDto[];
  isUploading: boolean;
  // Upload a new image (init → PUT → confirm). The library refreshes on success.
  upload: (file: File) => Promise<unknown>;
  remove: (fileId: string) => Promise<unknown>;
  // fileId → signed preview URL lookup (built from `images`), used by the
  // converter to render stored `{{image '<id>'}}` references on load.
  resolveImageUrl: (fileId: string) => string | undefined;
}

// Build a fileId → signed preview URL lookup from the media library, used by
// `useTemplateImageSupport` to populate `TemplateImageSupport.resolveImageUrl`.
export const buildImageUrlResolver = (
  images: TemplateImageDto[],
): ((fileId: string) => string | undefined) => {
  const byId = new Map(images.map((image) => [image.id, image.downloadUrl]));
  return (fileId: string) => byId.get(fileId);
};
